import React, { useMemo, useState } from 'react';
import { getAuthenticatedSupabase } from '../supabaseClient';
import type { Guest, GuestRelationshipLink, GuestRelationshipType } from '../types';
import { linksForGuest, normalizeGuestPair, otherGuestIdFromLink } from '../lib/guests/relationships';
import { Link2, Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/Button';

type TFn = (key: string) => string;

function hasDuplicatePairAndType(
  links: GuestRelationshipLink[],
  weddingId: string,
  typeId: string,
  low: string,
  high: string,
): boolean {
  return links.some(
    (l) =>
      l.wedding_id === weddingId &&
      l.relationship_type_id === typeId &&
      l.guest_low_id === low &&
      l.guest_high_id === high,
  );
}

interface GuestRelationshipPanelProps {
  weddingId: string;
  /** undefined = nový host ještě neuložený */
  guestId: string | undefined;
  guests: Guest[];
  relationshipTypes: GuestRelationshipType[];
  links: GuestRelationshipLink[];
  typeLabelById: Map<string, string>;
  onRefresh: () => Promise<void>;
  t: TFn;
  onToast: (type: 'success' | 'error', text: string) => void;
}

export const GuestRelationshipPanel: React.FC<GuestRelationshipPanelProps> = ({
  weddingId,
  guestId,
  guests,
  relationshipTypes,
  links,
  typeLabelById,
  onRefresh,
  t,
  onToast,
}) => {
  const [newTypeId, setNewTypeId] = useState<string>(() => relationshipTypes[0]?.id ?? '');
  const [guestQuery, setGuestQuery] = useState('');
  const [selectedOtherId, setSelectedOtherId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const guestById = useMemo(() => new Map(guests.map((g) => [g.id, g])), [guests]);

  const myLinks = useMemo(
    () => (guestId ? linksForGuest(links, guestId) : []),
    [links, guestId],
  );

  const candidates = useMemo(() => {
    if (!guestId) return [];
    const q = guestQuery.trim().toLowerCase();
    return guests.filter((g) => {
      if (g.id === guestId) return false;
      if (!q) return true;
      return g.full_name.toLowerCase().includes(q);
    });
  }, [guests, guestId, guestQuery]);

  React.useEffect(() => {
    if (relationshipTypes.length && !newTypeId) {
      setNewTypeId(relationshipTypes[0].id);
    }
  }, [relationshipTypes, newTypeId]);

  const addLink = async () => {
    if (!guestId || !selectedOtherId || !newTypeId) return;
    const [low, high] = normalizeGuestPair(guestId, selectedOtherId);
    if (low === high) {
      onToast('error', t('guests.relationships.error_self'));
      return;
    }
    if (hasDuplicatePairAndType(links, weddingId, newTypeId, low, high)) {
      onToast('error', t('guests.relationships.error_duplicate'));
      return;
    }
    setSaving(true);
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('guest_relationship_links').insert({
      wedding_id: weddingId,
      relationship_type_id: newTypeId,
      guest_low_id: low,
      guest_high_id: high,
    });
    setSaving(false);
    if (error) {
      onToast('error', error.message);
      return;
    }
    onToast('success', t('guests.relationships.link_added'));
    setGuestQuery('');
    setSelectedOtherId(null);
    await onRefresh();
  };

  const removeLink = async (linkId: string) => {
    setSaving(true);
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('guest_relationship_links').delete().eq('id', linkId);
    setSaving(false);
    if (error) {
      onToast('error', error.message);
      return;
    }
    onToast('success', t('guests.relationships.link_removed'));
    await onRefresh();
  };

  if (!guestId) {
    return (
      <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
        <h3 className="text-ds-meta text-slate-500 mb-2 flex items-center gap-2">
          <Link2 size={14} /> {t('guests.relationships.section_title')}
        </h3>
        <p className="text-ds-body text-slate-400 text-sm">{t('guests.relationships.save_guest_first')}</p>
      </div>
    );
  }

  if (relationshipTypes.length === 0) {
    return (
      <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
        <h3 className="text-ds-meta text-slate-500 mb-2 flex items-center gap-2">
          <Link2 size={14} /> {t('guests.relationships.section_title')}
        </h3>
        <p className="text-ds-body text-amber-600 dark:text-amber-400 text-sm">
          {t('guests.relationships.no_types_hint')}
        </p>
      </div>
    );
  }

  return (
    <div className="pt-4 border-t border-slate-100 dark:border-slate-800 space-y-4">
      <div>
        <h3 className="text-ds-meta text-slate-500 mb-1 flex items-center gap-2">
          <Link2 size={14} /> {t('guests.relationships.section_title')}
        </h3>
        <p className="text-ds-body text-slate-400 text-xs mb-3">{t('guests.relationships.section_hint')}</p>

        {myLinks.length > 0 && (
          <ul className="space-y-2 mb-4">
            {myLinks.map((link) => {
              const otherId = otherGuestIdFromLink(link, guestId);
              const other = guestById.get(otherId);
              const typeLabel = typeLabelById.get(link.relationship_type_id) ?? '—';
              return (
                <li
                  key={link.id}
                  className="flex items-center justify-between gap-2 py-2 px-3 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700"
                >
                  <div className="min-w-0">
                    <span className="text-ds-meta text-slate-400">{typeLabel}</span>
                    <span className="text-slate-300 mx-1.5">·</span>
                    <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{other?.full_name ?? otherId}</span>
                  </div>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => removeLink(link.id)}
                    className="p-2 text-slate-400 hover:text-red-500 rounded-lg shrink-0"
                    title={t('guests.relationships.remove')}
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-ds-meta text-slate-500 mb-1 block">{t('guests.relationships.type_label')}</label>
            <select
              value={newTypeId}
              onChange={(e) => setNewTypeId(e.target.value)}
              className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200 text-sm"
            >
              {relationshipTypes.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-ds-meta text-slate-500 mb-1 block">{t('guests.relationships.other_guest')}</label>
            <input
              type="text"
              value={guestQuery}
              onChange={(e) => {
                setGuestQuery(e.target.value);
                setSelectedOtherId(null);
              }}
              placeholder={t('guests.relationships.search_placeholder')}
              className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200 text-sm mb-2"
            />
            <div className="max-h-36 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
              {candidates.length === 0 ? (
                <div className="p-3 text-ds-meta text-slate-400 text-sm">{t('guests.relationships.empty_search')}</div>
              ) : (
                candidates.map((g) => {
                  const [low, high] = normalizeGuestPair(guestId, g.id);
                  const blocked =
                    newTypeId &&
                    hasDuplicatePairAndType(links, weddingId, newTypeId, low, high);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      disabled={blocked}
                      onClick={() => setSelectedOtherId(g.id)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        selectedOtherId === g.id
                          ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-800 dark:text-brand-200'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/80 text-slate-700 dark:text-slate-200'
                      } ${blocked ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      {g.full_name}
                      {blocked ? ` (${t('guests.relationships.already_linked')})` : ''}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <Button
          type="button"
          variant="secondary"
          className="mt-2 w-full sm:w-auto"
          disabled={saving || !selectedOtherId || !newTypeId}
          onClick={() => void addLink()}
          icon={<Plus size={16} />}
        >
          {t('guests.relationships.add_link')}
        </Button>
      </div>
    </div>
  );
};
